import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

// Two stacked photos fill the left column. Both bind to gallery shots so they
// stay coherent with the property, and editors can swap either independently.
const imageTopDef: SlotGroupDefinition = {
  label: 'Top Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[4].alt_text',
      defaultValue: 'Neighborhood scene',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick the most attractive community photo at this property — prefer a building exterior or an outdoor amenity. Never choose a photo showing bare dirt, construction, parking lots, dumpsters, or other unappealing scenes. Write alt text describing the chosen photo.',
    context:
      'Larger top image of a two-photo collage on the Neighborhood page, beside the neighborhood headline and blurb. Must be a different photo from the bottom collage image and the Neighborhood page hero banner.',
  },
};

const imageBottomDef: SlotGroupDefinition = {
  label: 'Bottom Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[5].alt_text',
      defaultValue: 'Neighborhood scene',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick another attractive, unused community photo — prefer a building exterior or an outdoor amenity, showing a different scene than the top image. Never choose a photo showing bare dirt, construction, parking lots, dumpsters, or other unappealing scenes. Write alt text describing the chosen photo.',
    context:
      'Smaller bottom image of a two-photo collage on the Neighborhood page, overlapping the top image. Must be a different photo from the top collage image and the Neighborhood page hero banner.',
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * Neighborhood page intro: a two-column stripe below the hero/wave band. The
 * left column stacks two gallery photos; the right column carries the headline
 * and a location blurb. Columns stay stacked through tablet so the collage and
 * the blurb each keep a full measure. The split starts at 1024px. The photo
 * pair stays capped under the column width, with a slightly larger overlap
 * from 1280px.
 */
export function NeighborhoodIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const imageTop = useSlotGroup('neighborhoodintro.imagetop', imageTopDef);
  const imageBottom = useSlotGroup('neighborhoodintro.imagebottom', imageBottomDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-primary)] py-12 md:py-16 xl:py-20"
    >
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-8 xl:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-10 xl:gap-16 items-center">
          {/* Left: stacked, staggered photos. The pair is capped well under
              the column width so neither shot fills the stripe. */}
          <div className="nbi-images mx-auto w-[88%] min-w-0 max-w-xs sm:max-w-sm md:max-w-md lg:mx-0 lg:w-[80%] lg:max-w-sm xl:w-[72%] xl:max-w-md">
            <EditableSlotGroup groupPath="neighborhoodintro.imagetop" definition={imageTopDef}>
              <div className="nbi-image-wrap nbi-image-wrap--square">
                {imageTop.image ? (
                  <VibesitesImage
                    url={imageTop.image}
                    alt={imageTop.alt || 'Neighborhood scene'}
                    className="nbi-image"
                    sizes="(max-width: 639px) 88vw, (max-width: 1023px) 448px, 384px"
                  />
                ) : (
                  <div className="nbi-image nbi-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="neighborhoodintro.imagebottom" definition={imageBottomDef}>
              <div className="nbi-image-wrap nbi-image-wrap--landscape">
                {imageBottom.image ? (
                  <VibesitesImage
                    url={imageBottom.image}
                    alt={imageBottom.alt || 'Neighborhood scene'}
                    className="nbi-image"
                    sizes="(max-width: 1023px) 260px, 240px"
                  />
                ) : (
                  <div className="nbi-image nbi-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>
          </div>

          {/* Right: headline + blurb. Heading size follows the column so a
              1024–1280px split doesn't inherit the wide-desktop scale. */}
          <div className="nbi-copy mx-auto w-full min-w-0 max-w-3xl lg:mx-0 lg:max-w-none">
            <RichTextContent
              path="neighborhoodintro.heading"
              label="Heading"
              defaultValue='<p>Explore the <span data-type="field-token" data-field="property_name"></span> Neighborhood</p>'
              as="h2"
              className="nbi-heading font-heading font-semibold uppercase tracking-wide text-[var(--vibe-color-text-on-primary)] leading-[1.05] mb-4 md:mb-5 xl:mb-6"
            />

            <RichTextContent
              path="neighborhoodintro.body"
              label="Body Text"
              defaultField="property_neighborhood_blurb"
              defaultValue='<p>Located in the vibrant Hyde Park neighborhood, <span data-type="field-token" data-field="property_name"></span> offers easy access to dining, shopping, and entertainment. Enjoy nearby attractions like Anderson Park and Hyde Park Village, all just minutes from home. With everything you need close by, <span data-type="field-token" data-field="property_name"></span> provides one of the best apartment living experiences in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>'
              as="div"
              className="font-body text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_p]:mb-4 [&_a]:text-[var(--vibe-color-text-on-primary)] [&_a]:underline"
            />
          </div>
        </div>
      </div>

      <style>{`
        .nbi-images {
          position: relative;
        }
        .nbi-copy {
          container-type: inline-size;
        }
        .nbi-heading {
          font-size: clamp(1.85rem, 7vw, 2.35rem);
        }
        .nbi-image-wrap {
          width: 100%;
          overflow: hidden;
          border-radius: 0;
        }
        .nbi-image {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }

        /* Top image: anchors the collage. Pulled in from the right edge so
           the smaller image can stick out past it. */
        .nbi-image-wrap--square {
          aspect-ratio: 4 / 3;
          width: calc(100% - 28px);
        }

        /* Bottom image: offset to the lower-right so it overlaps the larger
           image. Width and the negative margin are both relative to the
           capped collage, so the pair stays in proportion as it scales. */
        .nbi-image-wrap--landscape {
          aspect-ratio: 3 / 2;
          width: 52%;
          margin-left: auto;
          margin-top: -24%;
          position: relative;
          z-index: 2;
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.16);
        }

        /* Tablet, still stacked: a bit wider, with a lighter overlap. */
        @media (min-width: 640px) {
          .nbi-heading {
            font-size: clamp(2.15rem, 4vw, 2.75rem);
          }
          .nbi-image-wrap--square {
            aspect-ratio: 3 / 2;
            width: calc(100% - 36px);
          }
          .nbi-image-wrap--landscape {
            width: 48%;
            margin-top: -18%;
          }
        }

        /* Split layout: keep both shots compact inside the narrower column. */
        @media (min-width: 1024px) {
          .nbi-heading {
            font-size: clamp(1.75rem, 6.5cqi, 2.5rem);
          }
          .nbi-image-wrap--square {
            aspect-ratio: 4 / 3;
            width: calc(100% - 28px);
          }
          .nbi-image-wrap--landscape {
            width: 54%;
            margin-top: -22%;
            box-shadow: 0 14px 30px rgba(0, 0, 0, 0.16);
          }
        }

        /* Wide desktop: slightly larger than the laptop split, still well
           under a full column. */
        @media (min-width: 1280px) {
          .nbi-heading {
            font-size: clamp(2rem, 6.2cqi, 3rem);
          }
          .nbi-image-wrap--square {
            aspect-ratio: 4 / 3;
            width: calc(100% - 36px);
          }
          .nbi-image-wrap--landscape {
            width: 58%;
            margin-top: -26%;
            box-shadow: 0 16px 34px rgba(0, 0, 0, 0.16);
          }
        }

        .nbi-image--placeholder {
          background: var(--vibe-color-surface);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodIntro',
  displayName: 'Neighborhood Intro',
  tokens: {
    'neighborhoodintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Explore the <span data-type="field-token" data-field="property_name"></span> Neighborhood</p>',
    },
    'neighborhoodintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_neighborhood_blurb',
      defaultValue:
        '<p>Located in the vibrant Hyde Park neighborhood, <span data-type="field-token" data-field="property_name"></span> offers easy access to dining, shopping, and entertainment. Enjoy nearby attractions like Anderson Park and Hyde Park Village, all just minutes from home. With everything you need close by, <span data-type="field-token" data-field="property_name"></span> provides one of the best apartment living experiences in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>',
    },
  },
  slotGroups: {
    'neighborhoodintro.imagetop': imageTopDef,
    'neighborhoodintro.imagebottom': imageBottomDef,
  },
};

(NeighborhoodIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodIntro;
